import { z } from 'zod';

export const BeatTypeSchema = z.enum([
  'character',
  'place',
  'object',
  'event',
  'mood',
]);
export type BeatType = z.infer<typeof BeatTypeSchema>;

export const CardTypeSchema = z.enum([
  'twist',
  'rule_breaker',
  'chaos',
  'counter',
  'keepsake',
]);
export type CardType = z.infer<typeof CardTypeSchema>;

export const TrackSchema = z.enum(['hope', 'dread', 'chaos', 'fate']);
export type Track = z.infer<typeof TrackSchema>;

export const StoryElementSchema = z.object({
  id: z.string(),
  label: z.string(),
  beatType: BeatTypeSchema,
});
export type StoryElement = z.infer<typeof StoryElementSchema>;

export const CardDefSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: CardTypeSchema,
  cost: z.number().int().min(0).max(3),
  beatTypes: z.array(BeatTypeSchema),
  description: z.string(),
  flavor: z.string().optional(),
  upgradeId: z.string().optional(),
  isScar: z.boolean().optional(),
  rarity: z.enum(['paper', 'ink', 'foil', 'gold']).optional(),
});
export type CardDef = z.infer<typeof CardDefSchema>;

export const EffectSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('set_variable'),
    target: z.string(),
    value: z.union([z.string(), z.number(), z.boolean()]),
  }),
  z.object({
    type: z.literal('shift_track'),
    target: TrackSchema,
    value: z.number().int(),
  }),
  z.object({
    type: z.literal('shift_dread'),
    value: z.number().int(),
  }),
  z.object({
    type: z.literal('add_callback'),
    target: z.string(),
    value: z.string(),
  }),
  z.object({
    type: z.literal('apply_keepsake'),
    target: z.string(),
    value: z.string(),
  }),
  z.object({
    type: z.literal('narrator_glitch'),
    value: z.string(),
  }),
  z.object({
    type: z.literal('scene_mood'),
    value: z.enum(['calm', 'tense', 'chaos', 'triumph', 'dread']),
  }),
  z.object({
    type: z.literal('grant_energy'),
    value: z.number().int(),
  }),
]);
export type Effect = z.infer<typeof EffectSchema>;

export const OutcomeTierSchema = z.enum(['hero', 'template', 'fallback']);
export type OutcomeTier = z.infer<typeof OutcomeTierSchema>;

export const VisualRecipeSchema = z.object({
  backgroundId: z.string(),
  mood: z.string(),
  overlayIds: z.array(z.string()).optional(),
  narratorExpression: z.string().optional(),
  stampEffect: z.string().optional(),
});
export type VisualRecipe = z.infer<typeof VisualRecipeSchema>;

export const OutcomeSchema = z.object({
  key: z.string(),
  tier: OutcomeTierSchema,
  bridgeText: z.string(),
  narratorQuip: z.string(),
  effects: z.array(EffectSchema),
  callback: z.string().optional(),
  visual: VisualRecipeSchema,
});
export type Outcome = z.infer<typeof OutcomeSchema>;

export const SceneBeatSchema = z.object({
  id: z.string(),
  beatType: BeatTypeSchema,
  elementId: z.string(),
});
export type SceneBeat = z.infer<typeof SceneBeatSchema>;

export const SceneDefSchema = z.object({
  id: z.string(),
  chapterId: z.string().optional(),
  title: z.string(),
  narratorText: z.string(),
  intent: z.string(),
  intentTargetId: z.string().optional(),
  elements: z.array(StoryElementSchema),
  beats: z.array(SceneBeatSchema),
  choicePrompt: z.string().optional(),
  /** Reuse an existing background asset id when revisiting a place */
  backgroundReuseId: z.string().optional(),
  /** One-line goal for Mira in this scene (Quest panel). */
  goal: z.string().optional(),
  /** One-line "Previously" recap that also says why this scene follows the last. */
  previously: z.string().optional(),
  /** One-line canonical summary for the Story so far log once the scene ends. */
  summary: z.string().optional(),
});
export type SceneDef = z.infer<typeof SceneDefSchema>;

export const ChapterDefSchema = z.object({
  id: z.string(),
  number: z.number().int().min(1),
  title: z.string(),
  subtitle: z.string(),
  setpiece: z.string(),
  runningGag: z.string(),
  sceneStart: z.number().int().min(0),
  sceneEnd: z.number().int().min(0),
  titleCardText: z.string(),
});
export type ChapterDef = z.infer<typeof ChapterDefSchema>;

export const EndingDefSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  track: TrackSchema,
  minValue: z.number().int(),
  scarCardId: z.string(),
  /** Short label for the ending meter in the HUD. */
  meterLabel: z.string().optional(),
});
export type EndingDef = z.infer<typeof EndingDefSchema>;

export const CastMemberSchema = z.object({
  name: z.string(),
  role: z.string(),
});
export type CastMember = z.infer<typeof CastMemberSchema>;

export const PrologueBeatSchema = z.object({
  /** scene id whose painted plate illustrates this beat */
  art: z.string(),
  text: z.string(),
  /** show the narrator portrait with this expression */
  narrator: z.string().optional(),
});
export type PrologueBeat = z.infer<typeof PrologueBeatSchema>;

export const StoryPackSchema = z.object({
  id: z.string(),
  title: z.string(),
  book: z.number().int(),
  premise: z.string().optional(),
  quest: z.string().optional(),
  prologue: z.array(PrologueBeatSchema).optional(),
  /** Recurring characters keyed by element id: name tag + one-line role. */
  cast: z.record(CastMemberSchema).optional(),
  chapters: z.array(ChapterDefSchema).optional(),
  scenes: z.array(SceneDefSchema),
  endings: z.array(EndingDefSchema),
  variables: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
});
export type StoryPack = z.infer<typeof StoryPackSchema>;

export type DreadBand = 'low' | 'mid' | 'high';

export interface StateBucket {
  dominantTrack: Track;
  dreadBand: DreadBand;
  stateKey: string;
}

export interface RunMeta {
  storyId: string;
  chosenEndingId: string;
  runNumber: number;
  scarsUnlocked: string[];
  narratorMemories: string[];
}

export type GamePhase =
  | 'title'
  | 'run_setup'
  | 'chapter_intro'
  | 'scene_intro'
  | 'scene_play'
  | 'scene_resolve'
  | 'draft'
  | 'run_end'
  | 'recap';

export type RunScope = 'full' | 'chapter';

export interface PlayedCardRecord {
  cardId: string;
  beatId: string;
  sceneId: string;
  outcomeKey: string;
}

/** A plain-language record of what one card play changed (drives the What changed callout,
 * scene chips, callbacks and the end-of-scene summary). */
export interface ChangeRecord {
  sceneId: string;
  sceneIndex: number;
  sceneTitle: string;
  cardId: string;
  cardName: string;
  cardType: CardType;
  elementId: string;
  targetLabel: string;
  /** present tense, e.g. "Archivist Vell is now a frog." */
  text: string;
  /** past tense fragment for callbacks, e.g. "turned Archivist Vell into a frog" */
  did: string;
  /** short chip label, e.g. "frog" */
  chip: string;
  /** carries into later scenes when this element shows up again */
  persistent: boolean;
  /** the narrator's announced plan: blocked by this card, delayed, or still standing */
  plan: 'blocked' | 'delayed' | 'stands';
  dreadDelta: number;
  trackDeltas: Partial<Record<Track, number>>;
  moodBefore: string;
  moodAfter: string;
  /** the card type's risk or reward roll: surge, backfire, refund or guard */
  swing?: { kind: string; text: string };
}

/** How the narrator's plan ended up: blocked (counter), delayed, bent (2+ cards), stands (1 card), untouched (no cards). */
export type PlanResult = 'blocked' | 'delayed' | 'bent' | 'stands' | 'untouched';

export interface SceneLogEntry {
  sceneIndex: number;
  sceneId: string;
  title: string;
  summary: string;
  plays: ChangeRecord[];
  plan: PlanResult;
  /** Dread the plan itself added or removed at scene end */
  planDread?: number;
  dreadDelta: number;
  trackDeltas: Partial<Record<Track, number>>;
}

export interface SoloRunState {
  phase: GamePhase;
  runScope: RunScope;
  chapterId: string | null;
  runStartSceneIndex: number;
  runEndSceneIndex: number;
  pendingChapterId: string | null;
  sceneIndex: number;
  energy: number;
  maxEnergy: number;
  dread: number;
  tracks: Record<Track, number>;
  variables: Record<string, string | number | boolean>;
  deck: string[];
  hand: string[];
  discard: string[];
  drawPile: string[];
  callbacks: Record<string, string>;
  keepsakes: Record<string, string>;
  playedThisScene: PlayedCardRecord[];
  energySpentThisScene: number;
  chosenEndingId: string;
  endingReached: string | null;
  sceneMood: 'calm' | 'tense' | 'chaos' | 'triumph' | 'dread';
  lastOutcome: Outcome | null;
  draftOptions: string[];
  upgradeTargetId: string | null;
  narratorGlitchActive: boolean;
  /** every card play this run, in order */
  history?: ChangeRecord[];
  /** one entry per finished scene (Story so far) */
  sceneLog?: SceneLogEntry[];
  /** dread and tracks when the current scene started, for the end-of-scene summary */
  sceneStart?: { dread: number; tracks: Record<Track, number> };
  /** story element ids for characters the player has already met (for cast tags on return) */
  metCast?: string[];
}
